import { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { fetchRoute, gpxUrl, shareUrl } from "@/lib/api";
import { RouteMap } from "@/components/RouteMap";
import { ElevationProfile } from "@/components/ElevationProfile";
import { RouteSchema } from "@/components/RouteSchema";
import { Download, MapPin, Clock, Navigation, Mountain, Coffee, ShieldAlert, ArrowLeft, Flag, BedDouble, Utensils, Fuel, Camera, Trees, ParkingSquare, Share2, Check, CircleCheck, Circle, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { useRidden, waypointKey } from "@/lib/useRidden";

export default function RouteDetail() {
    const { slug } = useParams();
    const [route, setRoute] = useState(null);
    const [notFound, setNotFound] = useState(false);
    const ridden = useRidden(slug);

    useEffect(() => {
        setRoute(null);
        setNotFound(false);
        fetchRoute(slug)
            .then((r) => {
                setRoute(r);
                document.title = `${r.name} · MotorcycleRoutesIreland`;
            })
            .catch(() => setNotFound(true));
    }, [slug]);

    if (notFound) {
        return (
            <div data-testid="route-not-found" className="min-h-screen flex items-center justify-center px-6 pt-40">
                <div className="text-center">
                    <div className="eyebrow">404</div>
                    <h1 className="mt-4 font-display font-black uppercase text-[var(--fg)] text-5xl">Route not found</h1>
                    <Link to="/routes" className="mt-8 inline-block text-[#E87722] font-display uppercase tracking-[0.2em]">← Back to routes</Link>
                </div>
            </div>
        );
    }

    if (!route) {
        return <div data-testid="route-loading" className="min-h-screen flex items-center justify-center text-[var(--fg-dim)] font-body">Loading route…</div>;
    }

    return (
        <div data-testid="route-detail-page">
            <RouteSchema route={route} />
            {/* Hero */}
            <section className="relative min-h-[80vh] flex items-end overflow-hidden">
                <div className="absolute inset-0">
                    <img src={route.hero_image?.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${route.hero_image}` : route.hero_image} alt={route.name} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#14161A] via-[#14161A]/70 to-[#14161A]/20" />
                </div>
                <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-40 pb-16 w-full on-hero">
                    <Link
                        to="/routes"
                        data-testid="back-to-routes"
                        className="inline-flex items-center gap-2 text-[var(--fg-muted)] hover:text-[#E87722] font-display uppercase tracking-[0.2em] text-xs mb-8"
                    >
                        <ArrowLeft size={14} /> All Routes
                    </Link>
                    <div className="flex flex-wrap gap-2 mb-6">
                        <span className="px-3 py-1 bg-[#E87722] text-black font-display font-bold text-[11px] uppercase tracking-[0.2em]">{route.category}</span>
                        <span className="px-3 py-1 bg-black/60 backdrop-blur border border-white/20 text-[var(--fg)] font-display font-bold text-[11px] uppercase tracking-[0.2em]">{route.difficulty}</span>
                        <span className="px-3 py-1 bg-black/60 backdrop-blur border border-white/20 text-[var(--fg)] font-display font-bold text-[11px] uppercase tracking-[0.2em]">{route.region}</span>
                        {route.has_real_gpx && (
                            <span data-testid="detail-real-gpx-badge" className="px-3 py-1 bg-black/60 backdrop-blur border border-[#E87722] text-[#E87722] font-display font-bold text-[11px] uppercase tracking-[0.2em]">
                                ● Real GPX Track
                            </span>
                        )}
                    </div>
                    <h1 className="font-display font-black uppercase text-[var(--fg)] text-5xl md:text-7xl lg:text-8xl leading-[0.9] tracking-tighter max-w-4xl">
                        {route.name}
                    </h1>
                    <p className="mt-6 font-body text-[var(--fg-muted)] text-lg md:text-xl max-w-3xl leading-relaxed">
                        {route.summary}
                    </p>
                </div>
            </section>

            {/* Stats + Download */}
            <section className="border-y border-[var(--border-hair)] bg-[var(--surface)]">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 grid grid-cols-2 md:grid-cols-5 gap-8">
                    <Stat icon={Navigation} label="Distance" value={route.distance_km} unit="km" />
                    <Stat icon={Clock} label="Ride Time" value={route.duration_hours} unit="hours" />
                    <Stat icon={Mountain} label="Difficulty" value={route.difficulty} />
                    <Stat icon={MapPin} label="Region" value={route.region} />
                    <a
                        href={gpxUrl(route.slug)}
                        data-testid="download-gpx-btn"
                        className="group flex items-center justify-center gap-3 bg-[#E87722] hover:bg-[#F28C38] text-black px-6 py-4 font-display font-bold uppercase tracking-[0.2em] text-sm transition-all"
                    >
                        <Download size={18} strokeWidth={2.5} /> GPX File
                    </a>
                </div>
                <div className="max-w-7xl mx-auto px-6 lg:px-10 pb-8">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div className="text-xs uppercase tracking-[0.28em] text-[var(--fg-dim)] font-body">
                            Road type: <span className="text-[var(--fg-muted)]">{route.road_type}</span>
                        </div>
                        <ShareButton route={route} />
                    </div>
                </div>
            </section>

            {/* Map */}
            <section className="py-16 lg:py-20 bg-[var(--surface)]">
                <div className="max-w-7xl mx-auto px-6 lg:px-10">
                    <div className="eyebrow mb-3">The Route</div>
                    <h2 className="font-display font-black uppercase text-[var(--fg)] text-3xl md:text-5xl leading-none tracking-tight mb-8">
                        On the map.
                    </h2>
                    <RouteMap
                        center={route.center}
                        polyline={route.polyline}
                        waypoints={route.waypoints || []}
                        name={route.name}
                        height="560px"
                        ridden={ridden}
                    />
                    {route.elevation_profile && route.elevation_profile.length > 1 && (
                        <div className="mt-12">
                            <ElevationProfile
                                profile={route.elevation_profile}
                                gainM={route.elevation_gain_m}
                                distanceKm={route.distance_km}
                            />
                        </div>
                    )}
                </div>
            </section>

            {/* Story */}
            <section className="py-16 lg:py-24 border-t border-[var(--border-hair)]">
                <div className="max-w-4xl mx-auto px-6 lg:px-10">
                    <div className="eyebrow mb-3">The Ride</div>
                    <h2 className="font-display font-black uppercase text-[var(--fg)] text-4xl md:text-6xl leading-none tracking-tight mb-10">
                        What the road feels like.
                    </h2>
                    <div className="space-y-6 font-body text-lg leading-[1.85] text-[var(--fg-muted)]">
                        {route.story.map((p, i) => (
                            <p key={i} className={i === 0 ? "first-letter:font-display first-letter:text-6xl first-letter:font-black first-letter:text-[#E87722] first-letter:mr-2 first-letter:float-left first-letter:leading-none" : ""}>
                                {p}
                            </p>
                        ))}
                    </div>
                </div>
            </section>

            {/* Highlights + Cafes + Safety */}
            <section className="py-16 lg:py-24 border-t border-[var(--border-hair)] bg-[var(--surface-alt)]">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-3 gap-12">
                    <div>
                        <div className="flex items-center gap-3 mb-6">
                            <Flag size={18} className="text-[#E87722]" />
                            <div className="eyebrow !text-[var(--fg)]">Highlights</div>
                        </div>
                        <ul className="space-y-4">
                            {route.highlights.map((h, i) => (
                                <li key={i} className="flex gap-3 font-body text-[var(--fg-muted)]">
                                    <span className="text-[#E87722] font-display font-black w-8 flex-shrink-0">{String(i + 1).padStart(2, "0")}</span>
                                    <span>{h}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div>
                        <div className="flex items-center gap-3 mb-6">
                            <Coffee size={18} className="text-[#E87722]" />
                            <div className="eyebrow !text-[var(--fg)]">Cafés & Stops</div>
                        </div>
                        <ul className="space-y-6">
                            {route.cafes.map((c, i) => (
                                <li key={i} className="border-l-2 border-[#E87722] pl-4">
                                    <div className="font-display font-bold uppercase text-[var(--fg)] text-lg tracking-tight">{c.name}</div>
                                    <div className="text-xs uppercase tracking-[0.2em] text-[var(--fg-muted)] font-body mt-1">{c.town}</div>
                                    <p className="mt-2 font-body text-[var(--fg-muted)] text-sm leading-relaxed">{c.note}</p>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div>
                        <div className="flex items-center gap-3 mb-6">
                            <ShieldAlert size={18} className="text-[#E87722]" />
                            <div className="eyebrow !text-[var(--fg)]">Ride Safe</div>
                        </div>
                        <ul className="space-y-4">
                            {route.safety_notes.map((s, i) => (
                                <li key={i} className="flex gap-3 font-body text-[var(--fg-muted)] text-sm leading-relaxed">
                                    <span className="w-1.5 h-1.5 mt-2.5 bg-[#E87722] flex-shrink-0" />
                                    <span>{s}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </section>

            {/* Waypoints from GPX */}
            {route.waypoints && route.waypoints.length > 0 && (
                <WaypointsSection waypoints={route.waypoints} ridden={ridden} />
            )}

            {/* Gallery */}            {route.gallery.length > 0 && (
                <section className="py-16 lg:py-24 border-t border-[var(--border-hair)]">
                    <div className="max-w-7xl mx-auto px-6 lg:px-10">
                        <div className="eyebrow mb-3">Field Notes</div>
                        <h2 className="font-display font-black uppercase text-[var(--fg)] text-3xl md:text-5xl leading-none tracking-tight mb-10">
                            From the road.
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {route.gallery.map((img, i) => (
                                <div key={i} className="relative aspect-[4/5] overflow-hidden border border-[var(--border-hair)] group">
                                    <img src={img} alt={`${route.name} ${i + 1}`} className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105" />
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* Final CTA */}
            <section className="py-24 border-t border-[var(--border-hair)] text-center">
                <div className="max-w-3xl mx-auto px-6">
                    <h3 className="font-display font-black uppercase text-[var(--fg)] text-4xl md:text-5xl leading-none tracking-tight">
                        Take it with you.
                    </h3>
                    <p className="mt-4 font-body text-[var(--fg-muted)]">Download the GPX and load it into your navigation.</p>
                    <a
                        href={gpxUrl(route.slug)}
                        data-testid="download-gpx-cta"
                        className="mt-8 inline-flex items-center gap-3 bg-[#E87722] hover:bg-[#F28C38] text-black px-10 py-4 font-display font-bold uppercase tracking-[0.2em] transition-all"
                    >
                        <Download size={18} /> Download {route.slug}.gpx
                    </a>
                </div>
            </section>
        </div>
    );
}

const CATEGORIES = [
    { key: "all", label: "All", match: () => true },
    { key: "lodging", label: "Sleep", match: (s) => /lodging|hotel/i.test(s) },
    { key: "food", label: "Food & Drink", match: (s) => /restaurant|food|bar|pub|cafe/i.test(s) },
    { key: "fuel", label: "Fuel", match: (s) => /gas|fuel/i.test(s) },
    { key: "scenic", label: "Scenic", match: (s) => /scenic|camera/i.test(s) },
    { key: "park", label: "Parks & Nature", match: (s) => /^park|beach|nature|trees/i.test(s) },
    { key: "town", label: "Towns", match: (s) => /city|town/i.test(s) },
    { key: "other", label: "Other", match: (s) => !/lodging|hotel|restaurant|food|bar|pub|cafe|gas|fuel|scenic|camera|^park|beach|city|town/i.test(s || "") },
];

const WaypointsSection = ({ waypoints, ridden }) => {
    const [active, setActive] = useState("all");

    const counts = useMemo(() => {
        const c = {};
        CATEGORIES.forEach((cat) => {
            c[cat.key] = waypoints.filter((w) => cat.match(w.sym || "")).length;
        });
        return c;
    }, [waypoints]);

    const filtered = useMemo(() => {
        const cat = CATEGORIES.find((c) => c.key === active) || CATEGORIES[0];
        return waypoints.filter((w) => cat.match(w.sym || ""));
    }, [waypoints, active]);

    const availableCats = CATEGORIES.filter((c) => c.key === "all" || counts[c.key] > 0);

    // Ridden progress across the FULL waypoint list (not the filtered subset)
    const total = waypoints.length;
    const doneCount = useMemo(
        () => waypoints.filter((w) => ridden.isRidden(waypointKey(w))).length,
        [waypoints, ridden]
    );
    const pct = total === 0 ? 0 : Math.round((doneCount / total) * 100);

    const onReset = () => {
        ridden.clear();
        toast.success("Ride progress reset for this route.");
    };

    return (
        <section className="py-16 lg:py-24 border-t border-[var(--border-hair)]">
            <div className="max-w-7xl mx-auto px-6 lg:px-10">
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
                    <div>
                        <div className="eyebrow mb-3">Recommended Stops</div>
                        <h2 className="font-display font-black uppercase text-[var(--fg)] text-3xl md:text-5xl leading-none tracking-tight">
                            Pulled from the real GPX.
                        </h2>
                    </div>
                    <div className="text-[var(--fg-dim)] text-xs uppercase tracking-[0.24em] font-body">
                        {waypoints.length} waypoints · marked on map above
                    </div>
                </div>

                {/* Ridden progress bar */}
                <div data-testid="ridden-progress" className="mb-10 border border-[var(--border-hair)] bg-[var(--card-bg)] p-5">
                    <div className="flex items-end justify-between mb-3 flex-wrap gap-3">
                        <div className="flex items-baseline gap-3">
                            <div className="font-display font-black text-[#E87722] text-4xl md:text-5xl leading-none">{doneCount}<span className="text-[var(--fg-muted)] text-2xl md:text-3xl">/{total}</span></div>
                            <div className="text-[10px] uppercase tracking-[0.28em] text-[var(--fg-dim)] font-body">stops ridden</div>
                        </div>
                        <div className="flex items-center gap-4">
                            <div className="font-display font-bold text-[var(--fg)] text-lg">{pct}%</div>
                            {doneCount > 0 && (
                                <button
                                    type="button"
                                    onClick={onReset}
                                    data-testid="ridden-reset-btn"
                                    className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] text-[var(--fg-muted)] hover:text-[#E87722] transition-colors"
                                >
                                    <RotateCcw size={12} /> Reset
                                </button>
                            )}
                        </div>
                    </div>
                    <div className="h-1.5 bg-[var(--surface-alt)] overflow-hidden">
                        <div
                            className="h-full bg-[#E87722] transition-all duration-500 ease-out"
                            style={{ width: `${pct}%` }}
                            data-testid="ridden-progress-bar"
                        />
                    </div>
                    <div className="mt-3 text-xs text-[var(--fg-muted)] font-body">
                        Tap a stop below to mark it ridden. Saved on this device — no login needed.
                    </div>
                </div>

                <div className="flex flex-wrap gap-2 mb-10">
                    {availableCats.map((c) => (
                        <button
                            key={c.key}
                            data-testid={`waypoint-filter-${c.key}`}
                            onClick={() => setActive(c.key)}
                            className={`px-4 py-2 font-display font-bold uppercase tracking-[0.15em] text-xs border transition-all ${
                                active === c.key
                                    ? "bg-[#E87722] text-black border-[#E87722]"
                                    : "text-[var(--fg-muted)] border-[var(--border-hair)] hover:border-[#E87722] hover:text-[#E87722]"
                            }`}
                        >
                            {c.label}
                            <span className="ml-2 text-[10px] opacity-70">{c.key === "all" ? waypoints.length : counts[c.key]}</span>
                        </button>
                    ))}
                </div>

                {filtered.length === 0 ? (
                    <div className="text-[var(--fg-dim)] font-body text-sm py-8">No stops in this category.</div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {filtered.map((w, i) => {
                            const Icon = symIcon(w.sym);
                            const key = waypointKey(w);
                            const isDone = ridden.isRidden(key);
                            return (
                                <div
                                    key={key}
                                    data-testid={`waypoint-${i}`}
                                    className={`group relative flex items-start gap-3 p-4 border transition-all ${
                                        isDone
                                            ? "border-[#E87722] bg-[color:color-mix(in_srgb,#E87722_10%,var(--card-bg))]"
                                            : "border-[var(--border-hair)] bg-[var(--card-bg)] hover:border-[#E87722]"
                                    }`}
                                >
                                    <div className="w-9 h-9 flex-shrink-0 border border-[var(--border-hair)] flex items-center justify-center text-[#E87722]">
                                        <Icon size={16} />
                                    </div>
                                    <div className="min-w-0 flex-1 pr-8">
                                        <div className="font-display font-bold uppercase text-[var(--fg)] text-sm tracking-[0.05em] truncate">{w.name}</div>
                                        <div className="text-[10px] uppercase tracking-[0.24em] text-[var(--fg-dim)] mt-1">{w.sym || "Waypoint"}</div>
                                        {w.desc && <div className="mt-2 text-xs text-[var(--fg-muted)] font-body line-clamp-2">{w.desc}</div>}
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => ridden.toggle(key)}
                                        aria-pressed={isDone}
                                        aria-label={isDone ? `Mark ${w.name} unridden` : `Mark ${w.name} ridden`}
                                        data-testid={`ridden-toggle-${i}`}
                                        className={`absolute top-3 right-3 flex items-center justify-center rounded-full transition-all ${
                                            isDone
                                                ? "text-[#E87722] hover:text-[#F28C38]"
                                                : "text-[var(--fg-dim)] hover:text-[#E87722]"
                                        }`}
                                    >
                                        {isDone ? <CircleCheck size={22} strokeWidth={2} /> : <Circle size={22} strokeWidth={1.5} />}
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </section>
    );
};

const symIcon = (sym) => {
    const s = (sym || "").toLowerCase();
    if (s.includes("lodging") || s.includes("hotel")) return BedDouble;
    if (s.includes("restaurant") || s.includes("food")) return Utensils;
    if (s.includes("gas") || s.includes("fuel")) return Fuel;
    if (s.includes("scenic") || s.includes("camera")) return Camera;
    if (s.includes("park")) return s.includes("parking") ? ParkingSquare : Trees;
    if (s.includes("flag") || s.includes("triangle")) return Flag;
    return MapPin;
};

const Stat = ({ icon: Icon, label, value, unit }) => (
    <div className="flex items-start gap-3">
        <Icon size={18} className="text-[#E87722] mt-1" strokeWidth={2} />
        <div>
            <div className="text-[10px] uppercase tracking-[0.24em] text-[var(--fg-dim)] font-body">{label}</div>
            <div className="mt-1 flex items-baseline gap-2">
                <span className="font-display font-black text-3xl text-[var(--fg)] leading-none">{value}</span>
                {unit && <span className="text-xs uppercase tracking-[0.2em] text-[var(--fg-muted)]">{unit}</span>}
            </div>
        </div>
    </div>
);


const ShareButton = ({ route }) => {
    const [copied, setCopied] = useState(false);
    const share = async () => {
        const url = shareUrl(route.slug);
        const shareData = {
            title: route.name,
            text: `${route.name} — ${route.summary}`,
            url,
        };
        // Prefer native share (mobile) then clipboard fallback
        if (navigator.share) {
            try {
                await navigator.share(shareData);
                return;
            } catch (e) {
                if (e && e.name === "AbortError") return;
            }
        }
        try {
            await navigator.clipboard.writeText(url);
            setCopied(true);
            toast.success("Share link copied", {
                description: "Paste it into WhatsApp, Messages or anywhere — the card preview will render.",
            });
            setTimeout(() => setCopied(false), 2400);
        } catch (e) {
            toast.error("Couldn't copy link", { description: url });
        }
    };
    return (
        <button
            type="button"
            onClick={share}
            data-testid="share-route-btn"
            className="inline-flex items-center gap-2 border border-[var(--border-hair)] hover:border-[#E87722] hover:text-[#E87722] text-[var(--fg-muted)] px-4 py-2 font-display font-bold uppercase tracking-[0.2em] text-xs transition-colors"
        >
            {copied ? <Check size={14} strokeWidth={2.5} /> : <Share2 size={14} strokeWidth={2.5} />}
            {copied ? "Link Copied" : "Share Route"}
        </button>
    );
};
